import { Injectable } from '@angular/core';
import { AuthService } from './auth.service'; // Presumo che AuthService gestisca le chiamate API
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, filter, Observable, fromEvent, merge } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  private psicogeniusTokenDurationInSeconds: number = 60 * 60 * 8; // 8 hours
  private googleTokenDurationInSeconds: number = 60 * 60; // 1 hour

  private googleRefreshTimer: any = null;
  private psicogeniusRefreshTimer: any = null;

  // Osservabili per monitorare gli access token
  private googleAccessTokenSubject = new BehaviorSubject<string | null>(null);
  private psicogeniusAccessTokenSubject = new BehaviorSubject<string | null>(
    null
  );
  private readonly hasBrowserStorage =
    typeof window !== 'undefined' && typeof sessionStorage !== 'undefined';

  constructor(
    private authService: AuthService,
    private router: Router
  ) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.checkStatus();
      });

    // Add visibility change and focus handlers
    if (this.hasBrowserStorage) {
      merge(
        fromEvent(document, 'visibilitychange'),
        fromEvent(window, 'focus')
      ).subscribe(() => {
        this.handleTabActivation();
      });
    }

    // Initialize timers when the service is created
    this.initializeTimers();
  }

  checkStatus(): void {
    console.log('Checking service status after navigation:');
    console.log('Google Timer:', this.googleRefreshTimer);
    console.log('Psicogenius Timer:', this.psicogeniusRefreshTimer);
  }

  // ======== Google Access Token Management ========

  setGoogleAccessToken(token: string, expiresInSeconds: number): void {
    if (!this.hasBrowserStorage) return;
    sessionStorage.setItem('googleAccessToken', token);
    this.googleAccessTokenSubject.next(token);
    const expiryTimestamp = Date.now() + expiresInSeconds * 1000;
    sessionStorage.setItem('googleTokenExpiry', expiryTimestamp.toString());
    this.scheduleGoogleTokenRefresh(expiresInSeconds);
  }

  getGoogleAccessToken(): Observable<string | null> {
    if (!this.isGoogleTokenValid()) {
      this.refreshGoogleAccessToken();
    }
    if (!this.googleAccessTokenSubject.value) {
      this.refreshGoogleAccessToken();
    }
    return this.googleAccessTokenSubject.asObservable();
  }

  private scheduleGoogleTokenRefresh(expiresInSeconds: number): void {
    this.clearGoogleRefreshTimer();
    const fiveMinutesInSeconds = 300;
    const refreshTime = (expiresInSeconds - fiveMinutesInSeconds) * 1000;
    if (refreshTime > 0) {
      console.log(`Google token will refresh in ${refreshTime / 1000} seconds`);
      this.googleRefreshTimer = setTimeout(() => {
        this.refreshGoogleAccessToken();
      }, refreshTime);
    } else {
      console.warn('ExpiresIn too short, not setting Google refresh timer');
      this.router.navigate(['/']); // NOTE: this should never happen, but if it does, redirect to login
    }
  }

  refreshGoogleAccessToken(): void {
    this.authService.refreshGoogleAccessToken().subscribe({
      next: (response: any) => {
        this.setGoogleAccessToken(
          response.access_token,
          this.googleTokenDurationInSeconds
        );
      },
      error: (err) => {
        console.error('Failed to refresh Google access token', err);
        // this.router.navigate(['/']); // Redirigi alla pagina di login in caso di errore
      },
    });
  }

  private clearGoogleRefreshTimer(): void {
    if (this.googleRefreshTimer) {
      clearTimeout(this.googleRefreshTimer);
      this.googleRefreshTimer = null;
    }
  }

  // ======== Psicogenius Access Token Management ========

  setPsicogeniusAccessToken(token: string, expiresInSeconds: number): void {
    if (!this.hasBrowserStorage) return;
    sessionStorage.setItem('psicogeniusAccessToken', token);
    this.psicogeniusAccessTokenSubject.next(token);
    const expirationTimestamp = Date.now() + expiresInSeconds * 1000;
    sessionStorage.setItem(
      'psicogeniusTokenExpiry',
      expirationTimestamp.toString()
    );
    this.schedulePsicogeniusTokenRefresh(expiresInSeconds);
  }

  getPsicogeniusAccessToken(): Observable<string | null> {
    if (!this.isPsicogeniusTokenValid()) {
      this.refreshPsicogeniusAccessToken();
    }
    return this.psicogeniusAccessTokenSubject.asObservable();
  }

  private schedulePsicogeniusTokenRefresh(expiresInSeconds: number): void {
    this.clearPsicogeniusRefreshTimer();

    const fiveMinutesInSeconds = 300;
    const refreshInMilliSeconds =
      (expiresInSeconds - fiveMinutesInSeconds) * 1000;
    if (refreshInMilliSeconds > 0) {
      console.log(
        `Psicogenius token will refresh in ${refreshInMilliSeconds / 1000} seconds`
      );
      this.psicogeniusRefreshTimer = setTimeout(() => {
        this.refreshPsicogeniusAccessToken();
      }, refreshInMilliSeconds);
    } else {
      console.warn(
        'ExpiresInSeconds too short, not setting Psicogenius refresh timer'
      );
    }
  }

  private refreshPsicogeniusAccessToken(): void {
    this.authService.refreshPsicogeniusAccessToken().subscribe({
      next: (response: any) => {
        this.setPsicogeniusAccessToken(
          response.access_token,
          this.psicogeniusTokenDurationInSeconds
        );
      },
      error: (err) => {
        console.error('Failed to refresh Psicogenius access token', err);
        this.router.navigate(['/']); // Redirigi alla pagina di login in caso di errore
      },
    });
  }

  private clearPsicogeniusRefreshTimer(): void {
    if (this.psicogeniusRefreshTimer) {
      clearTimeout(this.psicogeniusRefreshTimer);
      this.psicogeniusRefreshTimer = null;
    }
  }

  // ======== Clear All Tokens on Logout ========

  clearAllTokens(): void {
    this.clearGoogleRefreshTimer();
    this.clearPsicogeniusRefreshTimer();
    if (this.hasBrowserStorage) {
      sessionStorage.clear(); // Elimina entrambi i token
    }
  }

  private initializeTimers(): void {
    if (!this.hasBrowserStorage) return;
    const googleExpiry = sessionStorage.getItem('googleTokenExpiry');
    const psicogeniusExpiry = sessionStorage.getItem('psicogeniusTokenExpiry');

    if (googleExpiry) {
      const expiresIn = Math.max(
        0,
        (parseInt(googleExpiry) - Date.now()) / 1000
      );
      this.scheduleGoogleTokenRefresh(expiresIn);
    }

    if (psicogeniusExpiry) {
      const expiresIn = Math.max(
        0,
        (parseInt(psicogeniusExpiry) - Date.now()) / 1000
      );
      this.schedulePsicogeniusTokenRefresh(expiresIn);
    }
  }

  private handleTabActivation(): void {
    if (!this.hasBrowserStorage) return;
    console.log('Handling tab activation');
    const fiveMinutesInMS = 300000;

    if (document.visibilityState === 'visible') {
      const googleExpiry = sessionStorage.getItem('googleTokenExpiry');
      const psicogeniusExpiry = sessionStorage.getItem(
        'psicogeniusTokenExpiry'
      );

      if (googleExpiry) {
        //this.refreshGoogleAccessToken();
        const timeUntilExpiryInMS = parseInt(googleExpiry) - Date.now();
        console.log(timeUntilExpiryInMS, 'timeUntilExpiry Google');

        if (timeUntilExpiryInMS < fiveMinutesInMS) {
          this.refreshGoogleAccessToken();
        } else {
          this.scheduleGoogleTokenRefresh(timeUntilExpiryInMS / 1000);
        }
      }

      if (psicogeniusExpiry) {
        const timeUntilExpiry = parseInt(psicogeniusExpiry) - Date.now();
        console.log(timeUntilExpiry, 'timeUntilExpiry Psicogenius');
        if (timeUntilExpiry < 0) {
          // Token has expired, clear tokens and redirect to login
          this.clearAllTokens();
          this.router.navigate(['/']);
        } else if (timeUntilExpiry < fiveMinutesInMS) {
          this.refreshPsicogeniusAccessToken();
        } else {
          this.schedulePsicogeniusTokenRefresh(timeUntilExpiry / 1000);
        }
        // this.refreshPsicogeniusAccessToken();
      }
    }
  }

  isGoogleTokenValid(): boolean {
    if (!this.hasBrowserStorage) return false;
    const expiry = sessionStorage.getItem('googleTokenExpiry');
    return expiry ? parseInt(expiry) > Date.now() : false;
  }

  isPsicogeniusTokenValid(): boolean {
    if (!this.hasBrowserStorage) return false;
    const expiry = sessionStorage.getItem('psicogeniusTokenExpiry');
    return expiry ? parseInt(expiry) > Date.now() : false;
  }
}
